import { AIService } from '../utils/aiService';
import type { AiRequestConfig, AiRequestState, AiMessage } from './aiRequestTypes';

export class AiRequest {
  private state: AiRequestState;
  private isDestroyed = false;

  constructor(private config: AiRequestConfig) {
    this.state = {
      status: 'starting',
      thinking: '',
      content: '',
      usage: { inputTokens: 0, outputTokens: 0 },
      webSearchSources: []
    };

    // Auto-start execution
    this.execute();
  }

  getState(): AiRequestState {
    return { ...this.state };
  }

  destroy(): void {
    this.isDestroyed = true;
  }

  private updateState(updates: Partial<AiRequestState>): void {
    if (this.isDestroyed) return;
    this.state = { ...this.state, ...updates };
  }

  private async execute(): Promise<void> {
    const { messages, apiKey, callbacks, system, enableThinking, enableWebSearch } = this.config;

    try {
      this.updateState({ status: 'starting' });
      callbacks?.onStart?.();

      // Convert DecryptedContentVersionDto to AiMessage format
      const aiMessages: AiMessage[] = messages.map(msg => ({
        role: msg.messageType === 'assistant' ? 'assistant' : 'user',
        content: msg.decryptedData || ''
      }));

      // Start streaming - no persistence, just pure AI interaction
      await AIService.sendMessageStream(aiMessages, {
        onStart: () => {
          this.updateState({ status: 'thinking' });
        },
        
        onThinking: (thinkingChunk) => {
          this.updateState({ thinking: this.state.thinking + thinkingChunk });
          callbacks?.onThinking?.(thinkingChunk);
        },
        
        onContent: (contentChunk) => {
          const newContent = this.state.content === '' ? contentChunk : this.state.content + contentChunk;
          this.updateState({ 
            status: 'responding',
            content: newContent
          });
          callbacks?.onContent?.(contentChunk);
        },
        
        onWebSearchSources: (sources) => {
          this.updateState({ 
            webSearchSources: [...(this.state.webSearchSources || []), ...sources]
          });
          callbacks?.onWebSearchSources?.(sources);
        },
        
        onComplete: async (finalContent, finalThinking, usage, webSearchSources) => {
          if (this.isDestroyed) return;
          
          // Use accumulated sources from streaming or the passed sources
          const finalSources = this.state.webSearchSources && this.state.webSearchSources.length > 0 
            ? this.state.webSearchSources 
            : webSearchSources || [];
          
          console.log('AiRequest onComplete - final sources:', finalSources);
          
          this.updateState({ 
            status: 'complete', 
            content: finalContent,
            thinking: finalThinking,
            usage,
            webSearchSources: finalSources
          });
          
          callbacks?.onComplete?.(this.state);
        },
        
        onError: (error) => {
          if (this.isDestroyed) return;
          this.updateState({ status: 'error' });
          callbacks?.onError?.(error);
        }
      }, {
        model: this.config.model || 'claude-4-sonnet-20250514',
        temperature: this.config.temperature || 1,
        maxTokens: this.config.maxTokens || 8192,
        apiKey,
        system,
        enableThinking,
        enableWebSearch
      });

    } catch (error) {
      if (this.isDestroyed) return;
      this.updateState({ status: 'error' });
      callbacks?.onError?.(error as Error);
    }
  }
}